import { isAllowedHost } from './allowlist';
import type { AdSlotSummary, DetectorOptions, MalvertisingHit } from './types';

const AD_SLOT_SELECTOR = 'ins.\\31 8ad69c2, ins[data-key]';

interface SlotInfo {
    key: string | null;
    testId: string | null;
    src: string | null;
    srcKey: string | null;
    host: string | null;
    outerHtml: string | null;
}

/** Turn a hit field into a string for TrackJS metadata. Arrays become newline-joined lists (readable
 *  in the dashboard); objects in an array are JSON-encoded per line (used by allSlots); null becomes
 *  the literal "null". */
function serializeMetaValue(value: unknown): string {
    if (value === null || value === undefined) return 'null';
    if (Array.isArray(value)) {
        if (!value.length) return '(none)';
        return value.map((v) => (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v))).join('\n');
    }
    return String(value);
}

/**
 * Detects forced ad redirects (malvertising) and reports them to TrackJS (native agent,
 * lazy-loaded on a hit). The observed campaign auto-redirects on page load with no user gesture.
 *
 * CONFIRMED MECHANISM (deobfuscated live C2 payload, 2026-07-01 — see docs/malvertising-repro/malvertising_report.md):
 * the creative sprays several TOP-navigation vectors, NOT window.open:
 *   1. window.top.location.href = re_l_url            (click_type=hj)
 *   2. window.top.eval("location.href = re_l_url")    (click_type=eval)
 *   3. synthetic <a target="_top" href=re_l_url>.click()  (click_type=pop)
 *   4. hidden <input> + blur-event fallback trigger
 *
 * KEY INSIGHT: all four mechanisms END in the SAME observable event — a TOP-LEVEL NAVIGATION to a
 * foreign (non-allowlisted) host. The blur in #4 is only a TRIGGER; its handler still sets
 * window.top.location. So the single reliable, false-positive-free signal is:
 *
 *     a top-level navigation to a NON-allowlisted host, NOT user-initiated, within N s of load.
 *
 * We do NOT listen for focus loss (blur / visibilitychange). That was the original design's mistake:
 * tab-switch, phone-lock and the notification shade all fire those events without any navigation, so
 * they produced massive false positives (a benign user tabbing away from a video). A tab switch is
 * NOT a navigation; malware IS. We key on the navigation, never on focus.
 *
 * VECTORS — every one checks the target is a NON-allowlisted host, so NONE can false-positive on
 * a same-origin nav or a tab switch (all gated on: foreign host + no interaction + within window):
 *  - navigate-api (primary): Navigation API `navigate` with userInitiated === false. Catches #1, #2
 *    and #4 (the blur-fallback's resulting location set), WITH the full target URL + click_type.
 *  - anchor-click (capture phase): catches #3's synthetic <a target="_top"> BEFORE navigation → URL.
 *    Works on every browser (no Navigation API needed).
 *  - window.open: cheap catch-all for popup-style malvertising; this campaign does NOT use it.
 *
 * We deliberately do NOT use a URL-less pagehide/beforeunload backstop: without the destination host
 * it cannot verify the nav is foreign, so it would fire on ordinary same-origin navigations (a real
 * false-positive source). Zero false positives is the hard requirement here, so every vector must
 * have a foreign-host check. The cost: on old Safari/iOS lacking the Navigation API, a top.location
 * redirect (#1/#2/#4) is missed — but the anchor-click vector (#3) still covers those browsers.
 *
 * SCOPE & LIMITATIONS (deliberate — NOT a universal redirect catcher):
 *  - NOT CAUGHT (by design, to keep false positives at zero): navigations the user initiated;
 *    same-origin / allowlisted-host navigations; navigations later than autoRedirectWindowMs;
 *    navigation entirely inside the cross-origin ad iframe with no top-level effect; and top.location
 *    redirects on old Safari/iOS without the Navigation API (anchor-click still covers those).
 *  - We CANNOT read the cross-origin iframe's rendered content or nested iframes (SecurityError).
 *    The request chain is approximated via resource-timing — see MalvertisingHit.suspiciousResources.
 *  - We usually CANNOT single out the offending slot (its iframe host is the allowlisted provider),
 *    so EVERY slot is captured in MalvertisingHit.allSlots; the real zone key lives on one <ins data-key>.
 */
export class MalvertisingDetector {
    private readonly options: DetectorOptions;
    private userInteracted = false;
    private readonly loadTime = performance.now();
    private reported = false;
    private originalOpen: typeof window.open;
    /** The wrapper we install on window.open, so stop() can check it's still ours before restoring. */
    private wrappedOpen: typeof window.open | null = null;
    /** Rolling set of non-allowlisted resource URLs seen anywhere on the page. Bounded to avoid growth. */
    private readonly suspiciousResources = new Set<string>();
    private static readonly MAX_RESOURCES = 40;

    // Bound handler references so stop() can detach them (production hygiene + test isolation).
    private readonly onInteract = (): void => {
        this.userInteracted = true;
    };
    private readonly onClickBound = (e: Event): void => this.onClick(e);
    private readonly onNavigateBound = (e: Event): void => this.onNavigate(e);
    private navTarget: EventTarget | null = null;

    constructor(options: DetectorOptions) {
        this.options = options;
        this.originalOpen = window.open.bind(window);
    }

    start(): void {
        // Forensics only, never a detection gate — see observeResources().
        this.observeResources();

        // A user gesture disarms the heuristic; capture-phase on window so it precedes element handlers.
        window.addEventListener('pointerdown', this.onInteract, { capture: true, once: true });
        window.addEventListener('keydown', this.onInteract, { capture: true, once: true });

        document.addEventListener('click', this.onClickBound, { capture: true });

        const nav = (window as unknown as { navigation?: EventTarget }).navigation;
        if (nav && typeof nav.addEventListener === 'function') {
            this.navTarget = nav;
            nav.addEventListener('navigate', this.onNavigateBound);
        }

        this.wrappedOpen = (...args: Parameters<typeof window.open>): ReturnType<typeof window.open> => {
            const url = args[0];
            if (url) {
                this.evaluate(String(url), 'window.open');
            }
            return this.originalOpen(...args);
        };
        window.open = this.wrappedOpen;
    }

    /** Detach all listeners and restore window.open. Called after a report (one-shot) and available
     *  for cleanup/tests. In production the page reload re-creates the detector fresh. */
    stop(): void {
        window.removeEventListener('pointerdown', this.onInteract, { capture: true });
        window.removeEventListener('keydown', this.onInteract, { capture: true });
        document.removeEventListener('click', this.onClickBound, { capture: true });
        this.navTarget?.removeEventListener('navigate', this.onNavigateBound);
        // Only restore if window.open is still OUR wrapper — another script (e.g. a popunder) may have
        // wrapped it after we did, and clobbering that would silently break it.
        if (window.open === this.wrappedOpen) window.open = this.originalOpen;
    }

    /** Capture-phase click handler: catch a synthetic anchor whose target is _top/_blank and whose
     *  href is a foreign host, before it navigates. This is the campaign's click_type=pop vector. */
    private onClick(event: Event): void {
        if (this.reported) return;
        const path = (event.composedPath?.() ?? []) as EventTarget[];
        const anchor = path.find((el): el is HTMLAnchorElement => el instanceof HTMLAnchorElement && !!el.href);
        if (!anchor) return;

        // A real user click sets userInteracted via pointerdown first; a synthetic .click() does not.
        const synthetic = !(event as { isTrusted?: boolean }).isTrusted;
        if (!synthetic && this.userInteracted) return;

        const target = (anchor.getAttribute('target') ?? '').toLowerCase();
        if (target !== '_top' && target !== '_blank' && target !== '_parent') return;
        this.evaluate(anchor.href, 'anchor-click');
    }

    private onNavigate(event: Event): void {
        if (this.reported) return;
        const e = event as { destination?: { url?: string }; userInitiated?: boolean; hashChange?: boolean };
        // A user clicking a link/submitting a form is userInitiated → not a forced redirect.
        // hashChange / same-document navigations are never a malvertising redirect.
        if (e.userInitiated || e.hashChange) return;
        const url = e.destination?.url;
        if (!url) return;
        this.evaluate(url, 'navigate-api');
    }

    /** Watch resource-timing entries for every network request made by ANY frame on the page,
     *  including inside cross-origin ad iframes and their nested frames. We can't read the iframe
     *  DOM, but we CAN see what it loads — which reconstructs the creative/redirect chain
     *  (e.g. magsrv/exoclick -> C2 -> landing page). Records only non-allowlisted hosts. */
    private observeResources(): void {
        if (typeof PerformanceObserver === 'undefined') return;
        try {
            const obs = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    const url = (entry as PerformanceResourceTiming).name;
                    if (!url) continue;
                    let host: string;
                    try {
                        host = new URL(url, window.location.href).host;
                    } catch {
                        continue;
                    }
                    if (!host || isAllowedHost(host, this.options.allowedHosts)) continue;
                    if (this.suspiciousResources.size < MalvertisingDetector.MAX_RESOURCES) {
                        this.suspiciousResources.add(url);
                    }
                }
            });
            obs.observe({ type: 'resource', buffered: true });
        } catch {
            // resource timing unavailable — the rest of the detector still works
        }
    }

    private evaluate(rawUrl: string, vector: MalvertisingHit['vector']): void {
        if (this.reported) return;

        let host: string;
        try {
            host = new URL(rawUrl, window.location.href).host;
        } catch {
            return;
        }

        if (!host || isAllowedHost(host, this.options.allowedHosts)) return;

        // A popup to a foreign host is only "forced" if the user hadn't interacted and it's
        // close to load. Genuine ad clicks set userInteracted=true and are excluded.
        const withinWindow = performance.now() - this.loadTime <= this.options.autoRedirectWindowMs;
        if (this.userInteracted || !withinWindow) return;

        this.report({ vector, targetUrl: rawUrl, targetHost: host });
    }

    /** Identify the offending ad slot: the slot whose child iframe points at a NON-allowlisted host.
     *  We deliberately do NOT fall back to a slot with an allowlisted (legit) iframe — blaming a clean
     *  slot is worse than reporting "unknown". Returns nulls if no foreign-iframe slot is found; the
     *  full candidate set is always captured separately in allSlots. */
    private findOffendingSlot(): SlotInfo {
        const slots = Array.from(document.querySelectorAll<HTMLElement>(AD_SLOT_SELECTOR));

        for (const slot of slots) {
            const iframe = slot.querySelector('iframe');
            const src = iframe?.getAttribute('src') ?? null;
            if (!src) continue;

            try {
                const parsed = new URL(src, window.location.href);
                if (parsed.host && !isAllowedHost(parsed.host, this.options.allowedHosts)) {
                    return this.describeSlot(slot, src, parsed);
                }
            } catch {
                // Unparseable iframe src is itself suspicious → treat as offending.
                return this.describeSlot(slot, src, null);
            }
        }

        return { key: null, testId: null, src: null, srcKey: null, host: null, outerHtml: null };
    }

    /** Extract everything reachable about an offending slot. NOTE: the iframe's rendered content
     *  and any nested iframes are cross-origin and cannot be read from page JS (SecurityError) —
     *  only element attributes are available. The load chain is captured separately via resource timing. */
    private describeSlot(slot: HTMLElement, src: string, parsed: URL | null): SlotInfo {
        // Cap outerHTML so a bloated slot can't blow the beacon size limit.
        const outerHtml = slot.outerHTML.length > 2000 ? `${slot.outerHTML.slice(0, 2000)}…` : slot.outerHTML;
        return {
            key: slot.dataset.key ?? null,
            testId: slot.getAttribute('data-testid'),
            src,
            // ptgncdn/exoclick pass the zone key in the iframe src as ?key=… — a 2nd source of truth.
            srcKey: parsed?.searchParams.get('key') ?? null,
            host: parsed?.host ?? null,
            outerHtml,
        };
    }

    /** Summarize EVERY ad slot on the page. This is the reliable zone signal: on a real hit the
     *  offending iframe host is the allowlisted provider, so findOffendingSlot() returns null — but the
     *  true zone key is here on one of these <ins data-key> entries, alongside the iframe host, the
     *  key= param, and the sandbox (which tells us whether the creative could top-navigate at all). */
    private summarizeSlots(): AdSlotSummary[] {
        return Array.from(document.querySelectorAll<HTMLElement>(AD_SLOT_SELECTOR)).map((slot) => {
            const iframe = slot.querySelector('iframe');
            const src = iframe?.getAttribute('src') ?? null;
            let host: string | null = null;
            let iframeSrcKey: string | null = null;
            if (src) {
                try {
                    const parsed = new URL(src, window.location.href);
                    host = parsed.host || null;
                    iframeSrcKey = parsed.searchParams.get('key');
                } catch {
                    // Unparseable src → leave host null; the raw slot is still captured below.
                }
            }
            return {
                key: slot.dataset.key ?? null,
                testId: slot.getAttribute('data-testid'),
                iframeHost: host,
                iframeSrcKey,
                foreign: !!host && !isAllowedHost(host, this.options.allowedHosts),
                iframeSandbox: iframe?.getAttribute('sandbox') ?? null,
            };
        });
    }

    private report(partial: Pick<MalvertisingHit, 'vector' | 'targetUrl' | 'targetHost'>): void {
        this.reported = true; // one report per pageview

        const slot = this.findOffendingSlot();
        const hit: MalvertisingHit = {
            zoneKey: slot.key,
            zoneTestId: slot.testId,
            creativeSrc: slot.src,
            creativeSrcKey: slot.srcKey,
            creativeHost: slot.host,
            slotOuterHtml: slot.outerHtml,
            suspiciousResources: Array.from(this.suspiciousResources),
            allSlots: this.summarizeSlots(),
            targetUrl: partial.targetUrl,
            targetHost: partial.targetHost,
            clickType: this.parseClickType(partial.targetUrl),
            vector: partial.vector,
            pageUrl: window.location.href,
            pageId: document.body?.dataset.pageId ?? null,
            msSinceLoad: Math.round(performance.now() - this.loadTime),
            userInteracted: this.userInteracted,
            avSegment: this.readAvSegment(),
            consentPolicy: this.readConsentPolicy(),
        };

        // Visible in console if anyone has devtools open, and captured by the agent's own
        // console telemetry once it is installed.
        const summary =
            `[MALVERTISING] forced redirect via ${hit.vector}: zone=${hit.zoneKey ?? 'unknown'} ` +
            `(${hit.zoneTestId ?? 'no-testid'}) → ${hit.targetHost}`;
        console.error(summary);

        // Report via the native TrackJS agent (lazy-loaded on a hit). The raw Capture API was
        // tried and confirmed NOT to render in this account's dashboard, so the agent — which
        // builds the payload shape the dashboard displays — is the only reliable path.
        this.trackViaAgent(hit, summary);

        // One-shot: detach listeners so we never double-report and don't leak handlers.
        this.stop();
    }

    /** Ensure the native TrackJS agent is installed, then track() with metadata. */
    private trackViaAgent(hit: MalvertisingHit, summary: string): void {
        const send = (): void => {
            try {
                const tjs = window.TrackJS;
                if (!tjs) return;
                for (const [key, value] of Object.entries(hit)) {
                    tjs.addMetadata(key, serializeMetaValue(value));
                }
                tjs.track(new Error(summary));
            } catch {
                /* reporting is best-effort; never throw into the page */
            }
        };

        try {
            if (window.TrackJS?.isInstalled()) {
                send();
                return;
            }
            const script = document.createElement('script');
            script.src = this.options.trackJsAgentSrc;
            script.addEventListener('load', (): void => {
                try {
                    if (window.TrackJS && !window.TrackJS.isInstalled()) {
                        window.TrackJS.install({
                            token: this.options.trackJsToken,
                            application: this.options.application,
                        });
                    }
                    send();
                } catch {
                    /* best-effort */
                }
            });
            document.head.appendChild(script);
        } catch {
            /* best-effort */
        }
    }

    private readAvSegment(): string {
        try {
            const av = window.getAVCookie?.();
            if (!av) return 'none';
            return `${av.type}:required=${av.required}:guest=${av.isGuest}`;
        } catch {
            return 'unknown';
        }
    }

    private readConsentPolicy(): string | null {
        try {
            return window.getAdConsentPolicy?.() ?? null;
        } catch {
            return null;
        }
    }

    /** The campaign tags the redirect URL with ?click_type=hj|eval|pop to signal which mechanism
     *  fired. Extract it when we have a real target URL. */
    private parseClickType(targetUrl: string): string | null {
        try {
            return new URL(targetUrl, window.location.href).searchParams.get('click_type');
        } catch {
            return null;
        }
    }
}
